import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Alert
} from '@mui/material';
import { 
  AccountBalanceWallet, 
  CreditCard, 
  Edit, 
  Save,
  Cancel,
  TrendingUp,
  Person
} from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faWallet,
  faCreditCard,
  faDollarSign,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { updateCreditLimit } from '../../services/financeService';
import TransactionListAdmin from './TransactionListAdmin';
import AddCreditAdminModal from '../../components/Admin/AddCreditAdminModal';

const WalletAdminView = ({ userWallet, userTransactions, onDataRefresh, selectedUser }) => {
  const [creditLimit, setCreditLimit] = useState(userWallet?.credit_limit);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(null);
  const [isAddCreditModalOpen, setAddCreditModalOpen] = useState(false);

  const handleSaveCreditLimit = async () => {
    if (isNaN(creditLimit) || creditLimit < 0) {
      setError('Por favor, introduce un límite de crédito válido.');
      return;
    }
    setError(null);
    try {
      await updateCreditLimit(userWallet.wallet_id, parseFloat(creditLimit));
      setEditing(false);
      onDataRefresh();
    } catch (err) {
      setError('Error al actualizar el límite de crédito.');
      console.error(err);
    }
  };

  return (
    <Box>
      {/* Header de información financiera - Se muestra cuando hay un usuario seleccionado */}
      {selectedUser ? (
        userWallet ? (
          <Card sx={{
            borderRadius: 3,
            boxShadow: '0 2px 12px rgba(0, 38, 66, 0.08)',
            border: '1px solid rgba(0, 38, 66, 0.06)',
            mb: 3
          }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <Box sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                mb: 3
              }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #002642 0%, #00577A 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Person sx={{ color: '#FFFFFF', fontSize: '1.2rem' }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#002642',
                    fontWeight: 600,
                    fontSize: { xs: '1rem', sm: '1.125rem' }
                  }}
                >
                  Información Financiera de {selectedUser.username}
                </Typography>
              </Box>

              <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Saldo Actual */}
                <Grid item xs={12} md={6}>
                  <Box sx={{
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: { xs: 2, sm: 3 },
                    backgroundColor: '#FFFFFF',
                    border: '1px solid rgba(0, 38, 66, 0.06)',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 16px rgba(0, 38, 66, 0.1)',
                      transform: 'translateY(-2px)'
                    }
                  }}>
                    <FontAwesomeIcon
                      icon={faWallet}
                      style={{
                        color: '#DB9600',
                        fontSize: '2rem',
                        marginBottom: '12px'
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#002642',
                        fontSize: '0.8rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        mb: 1,
                        fontWeight: 600
                      }}
                    >
                      Saldo Actual
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        color: userWallet.balance >= 0 ? '#4CAF50' : '#F44336',
                        fontWeight: 700,
                        fontSize: { xs: '1.5rem', sm: '2rem' }
                      }}
                    >
                      ${userWallet.balance.toFixed(2)}
                    </Typography>
                    <Chip
                      label={userWallet.balance >= 0 ? 'Positivo' : 'Negativo'}
                      size="small"
                      sx={{
                        mt: 1,
                        backgroundColor: userWallet.balance >= 0 ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                        color: userWallet.balance >= 0 ? '#4CAF50' : '#F44336',
                        fontWeight: 600
                      }}
                    />
                  </Box>
                </Grid>

                {/* Límite de Crédito */}
                <Grid item xs={12} md={6}>
                  <Box sx={{
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: { xs: 2, sm: 3 },
                    backgroundColor: '#FFFFFF',
                    border: '1px solid rgba(0, 38, 66, 0.06)',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 16px rgba(0, 38, 66, 0.1)',
                      transform: 'translateY(-2px)'
                    }
                  }}>
                    <FontAwesomeIcon
                      icon={faCreditCard}
                      style={{
                        color: '#DB9600',
                        fontSize: '2rem',
                        marginBottom: '12px'
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#002642',
                        fontSize: '0.8rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        mb: 1,
                        fontWeight: 600
                      }}
                    >
                      Límite de Crédito
                    </Typography>
                    
                    {editing ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <TextField
                          type="number"
                          value={creditLimit}
                          onChange={(e) => setCreditLimit(e.target.value)}
                          size="small"
                          InputProps={{
                            startAdornment: <FontAwesomeIcon icon={faDollarSign} style={{ color: '#75787B', fontSize: '0.9rem', marginRight: '8px' }} />
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2
                            }
                          }}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            onClick={handleSaveCreditLimit}
                            variant="contained"
                            size="small"
                            startIcon={<Save />}
                            sx={{
                              background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
                              textTransform: 'none',
                              fontSize: '0.75rem'
                            }}
                          >
                            Guardar
                          </Button>
                          <Button
                            onClick={() => {
                              setEditing(false);
                              setCreditLimit(userWallet?.credit_limit);
                              setError(null);
                            }}
                            variant="outlined"
                            size="small"
                            startIcon={<Cancel />}
                            sx={{
                              borderColor: '#75787B',
                              color: '#75787B',
                              textTransform: 'none',
                              fontSize: '0.75rem'
                            }}
                          >
                            Cancelar
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <>
                        <Typography
                          variant="h4"
                          sx={{
                            color: '#002642',
                            fontWeight: 700,
                            fontSize: { xs: '1.5rem', sm: '2rem' },
                            mb: 1
                          }}
                        >
                          ${userWallet.credit_limit.toFixed(2)}
                        </Typography>
                        <Button
                          onClick={() => setEditing(true)}
                          variant="outlined"
                          size="small"
                          startIcon={<Edit />}
                          sx={{
                            borderColor: '#002642',
                            color: '#002642',
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            '&:hover': {
                              borderColor: '#002642',
                              backgroundColor: 'rgba(0, 38, 66, 0.04)'
                            }
                          }}
                        >
                          Editar
                        </Button>
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 2
                  }}
                >
                  {error}
                </Alert>
              )}

              <Divider sx={{ my: 3 }} />

              <Button
                variant="contained"
                onClick={() => setAddCreditModalOpen(true)}
                sx={{
                  margin: 2,
                  background: 'linear-gradient(135deg, #002642 0%, #00577A 100%)',
                }}
              >
                Añadir Recarga
              </Button>

            </CardContent>
          </Card>
        ) : (
          <Card sx={{
            borderRadius: 3,
            boxShadow: '0 2px 12px rgba(0, 38, 66, 0.08)',
            border: '1px solid rgba(0, 38, 66, 0.06)',
            mb: 3,
            textAlign: 'center'
          }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <Typography variant="body1" color="text.secondary">
                Este usuario no tiene una billetera asignada.
              </Typography>
            </CardContent>
          </Card>
        )
      ) : (
        <Card sx={{
          borderRadius: 3,
          boxShadow: '0 2px 12px rgba(0, 38, 66, 0.08)',
          border: '1px solid rgba(0, 38, 66, 0.06)',
          mb: 3,
          textAlign: 'center'
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
            <Typography variant="h6" sx={{ color: '#002642', fontWeight: 600, mb: 1 }}>
              Seleccione un usuario
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Busque y seleccione un usuario para ver su información financiera.
            </Typography>
          </CardContent>
        </Card>
      )}

        {/* Header para transacciones generales cuando no hay usuario */}
        {!selectedUser && (
          <Card sx={{ 
            borderRadius: 3,
            boxShadow: '0 2px 12px rgba(0, 38, 66, 0.08)',
            border: '1px solid rgba(0, 38, 66, 0.06)',
            mb: 3
          }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2, 
                mb: 0
              }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #002642 0%, #00577A 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <TrendingUp sx={{ color: '#FFFFFF', fontSize: '1.2rem' }} />
                </Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#002642',
                    fontWeight: 600,
                    fontSize: { xs: '1rem', sm: '1.125rem' }
                  }}
                >
                  Vista General de Transacciones
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Lista de transacciones */}
        <TransactionListAdmin
          transactions={userTransactions}
          onDataRefresh={onDataRefresh}
          isUserView={!!selectedUser}
        />
 
     <AddCreditAdminModal
       open={isAddCreditModalOpen}
       onClose={() => setAddCreditModalOpen(false)}
       userId={selectedUser?.id}
       onCreditAdded={() => {
         setAddCreditModalOpen(false);
         onDataRefresh();
       }}
     />
   </Box>
 );
};

export default WalletAdminView;